import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to manage rotating placeholder suggestions
 * @param {Array<string>} suggestions - Array of placeholder suggestions to rotate
 * @param {number} interval - Interval in milliseconds between placeholder changes (default: 3000ms)
 * @param {boolean} paused - When true, rotation is frozen (e.g. while the user is typing)
 * @returns {string} Current placeholder text
 */
const useRotatingPlaceholder = (suggestions = [], interval = 3000, paused = false) => {
  const [currentPlaceholder, setCurrentPlaceholder] = useState(
    suggestions.length > 0 ? suggestions[0] : "Search coaches..."
  );
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  // Track the previous suggestions reference so we only reset when the list changes
  const prevSuggestionsRef = useRef(suggestions);

  useEffect(() => {
    if (!suggestions || suggestions.length === 0) return;

    // Clear existing interval if running
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Only reset to index 0 when the suggestions list itself changes (e.g. smart ↔ advanced toggle)
    // Do NOT reset when paused changes (user typing/clearing), so the animation resumes from where it left off
    if (prevSuggestionsRef.current !== suggestions) {
      prevSuggestionsRef.current = suggestions;
      setCurrentPlaceholder(suggestions[0]);
      setIndex(0);
    }

    // Don't start a new interval while paused
    if (paused) return;

    // Create new interval
    timerRef.current = setInterval(() => {
      setIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % suggestions.length;
        setCurrentPlaceholder(suggestions[nextIndex]);
        return nextIndex;
      });
    }, interval);

    // Cleanup on unmount or dependency change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [suggestions, interval, paused]);

  return currentPlaceholder;
};

export default useRotatingPlaceholder;
